import React, { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Login } from "@microsoft/mgt-react";
import MicrosoftSignInButton from "./MicrosoftSignInButton";
import {
  FiArchive,
  FiCheckSquare,
  FiFileText,
  FiHome,
  FiInbox,
  FiSettings,
  FiUsers,
} from "react-icons/fi";

function Header({
  isSignedIn,
  user,
  checkGroupAdresse,
  checkGroupAdmin,
  checkGroupNom,
  checkGroupReseauInfo,
  checkGroupInfoBancaire,
  checkGroupAntCriminels,
  checkGroupRecMensuelle,
  checkGroupSeancePlacement,
  checkGroupTeletravail,
  checkGroupConsultationAchatEI,
  routesConfig,
  existMembre
}) {
  const navigate = useNavigate();
  const [isInactive, setIsInactive] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null); // Reference for the dropdown menu
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(120); // 2 minutes = 120 secondes
  const [mobileOpen, setMobileOpen] = useState(false);
  // const toggleMobileMenu = () => setMobileOpen(!mobileOpen);
  const asideRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        asideRef.current &&
        !asideRef.current.contains(event.target) &&
        mobileOpen
      ) {
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const loginComponent = document.querySelector("mgt-login");

    const handleLogout = () => {
      navigate("/");
    };

    const handleLoginError = (event) => {
      console.error("Login error:", event.detail);
    };

    if (loginComponent) {
      loginComponent.addEventListener("logoutInitiated", handleLogout);
      loginComponent.addEventListener("loginError", handleLoginError);
    }

    return () => {
      if (loginComponent) {
        loginComponent.removeEventListener("logoutInitiated", handleLogout);
        loginComponent.removeEventListener("loginError", handleLoginError);
      }
    };
  }, [navigate]);

  useEffect(() => {
    if (!isSignedIn) return;

    const destination =
      localStorage.getItem("redirectAfterLogin") ||
      sessionStorage.getItem("redirectAfterLogin");
    if (!destination) return;

    const destinationPath = destination.split("?")[0];
    const allowedDestinations = new Set([
      "/demande",
      "/taches",
      "/formulaire",
      "/EntenteTravailHybrid",
      "/achatEquipInfo",
      "/participationFormation",
    ]);

    if (!allowedDestinations.has(destinationPath)) {
      localStorage.removeItem("redirectAfterLogin");
      sessionStorage.removeItem("redirectAfterLogin");
      return;
    }

    const dynamicDestinationPaths = new Set([
      "/formulaire",
      "/EntenteTravailHybrid",
      "/achatEquipInfo",
    ]);
    const destinationRouteIsReady =
      !dynamicDestinationPaths.has(destinationPath) ||
      routesConfig.some((route) => route.path === destinationPath);

    if (!destinationRouteIsReady) return;

    localStorage.removeItem("redirectAfterLogin");
    sessionStorage.removeItem("redirectAfterLogin");
    navigate(destination, { replace: true });
  }, [isSignedIn, navigate, routesConfig]);

  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const hasTaskManagerRole = Boolean(
    checkGroupAdresse ||
      checkGroupNom ||
      checkGroupReseauInfo ||
      checkGroupInfoBancaire ||
      checkGroupAntCriminels ||
      checkGroupRecMensuelle ||
      checkGroupSeancePlacement ||
      checkGroupTeletravail ||
      checkGroupConsultationAchatEI
  );

  const roleLabel = checkGroupAdmin
    ? "Administrateur"
    : hasTaskManagerRole
      ? "Gestionnaire de tâches"
      : "Utilisateur";

  useEffect(() => {
    let inactivityTimeout;
    let warningTimeout;
    let countdownInterval;

    const resetTimer = () => {
      clearTimeout(inactivityTimeout);
      clearTimeout(warningTimeout);
      clearInterval(countdownInterval);
      setShowWarning(false);
      // setCountdown(120);
      // Determine timeout based on route
      const isShortTimeout =
        location.pathname === "/taches" || location.pathname === "/groupRoles";
      const totalInactivity = isShortTimeout ? 30 * 60 * 1000 : 60 * 60 * 1000; // 15 or 40 minutes
      const warningTime = isShortTimeout ? 28 * 60 * 1000 : 58 * 60 * 1000; // 2 minutes before logout

      const countdownStart = 120; // seconds
      setCountdown(countdownStart);

      // Affiche l’alerte après 18 min
      warningTimeout = setTimeout(() => {
        setShowWarning(true);
        // setCountdown(120); // recommence à 120 sec
        setCountdown(countdownStart);
        countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, warningTime);

      inactivityTimeout = setTimeout(() => {
        setIsInactive(true);
        setShowWarning(false);
        clearInterval(countdownInterval);
      }, totalInactivity); // 15 minutes
    };

    window.onload = resetTimer;
    window.onmousemove = resetTimer;
    window.onmousedown = resetTimer;
    window.ontouchstart = resetTimer;
    window.onclick = resetTimer;
    window.onkeydown = resetTimer;

    return () => {
      clearTimeout(inactivityTimeout);
      clearTimeout(warningTimeout);
      clearInterval(countdownInterval);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isInactive) {
      const loginComponent = document.querySelector("mgt-login");
      if (loginComponent) {
        loginComponent.logout();
      }
    }
  }, [isInactive]);

  // // Close dropdown when clicking outside
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
  //       setDropdownOpen(false);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);

  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, []);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const openFormsMenu = () => {
    setDropdownOpen(true);
    if (window.matchMedia("(max-width: 767px)").matches) {
      setMobileOpen(true);
    }
  };

  // const handleOptionClick = () => {
  //   setDropdownOpen(false); // Close the dropdown when an option is clicked
  // };

  return (
    <div className="flex min-h-screen ">
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-950/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Fermer la navigation"
        />
      )}

      {/* Sidebar */}
      {/* <aside className="min-w-48 max-w-[14%] w-auto min-h-full bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white p-6 shadow-2xl flex flex-col transition-all duration-300"> */}
      <aside
        id="mobile-navigation"
        ref={asideRef}
        className={`fixed top-0 left-0 z-50 h-screen w-56 min-w-56 overflow-y-auto bg-[#0f2742] text-white py-6 shadow-xl flex flex-col transition-transform duration-300
  ${mobileOpen ? "translate-x-0" : "-translate-x-full"} 
  md:relative md:z-auto md:h-auto md:min-h-full md:translate-x-0 md:overflow-visible`}
      >
        {/* Logo */}
        <NavLink
          to="/"
          onClick={() => setMobileOpen(false)}
          className="mx-auto mb-10 block"
          aria-label="Retour à l’accueil"
        >
          <img
            src={`${process.env.PUBLIC_URL}/csv.png`}
            alt="Conseil scolaire Viamonde"
            className="h-auto w-48 transition-transform duration-300 hover:scale-105"
          />
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1">
          <ul className="flex flex-col gap-1">
            {isSignedIn && (
              <>
                <li>
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      `flex w-full items-center gap-3 border-l-4 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? "border-blue-400 bg-[#243e5e] text-white"
                          : "border-transparent text-gray-300 hover:bg-[#1a334f] hover:text-white"
                      }`
                    }
                  >
                    <FiHome className="h-4 w-4 flex-shrink-0 text-blue-200" />
                    Accueil
                  </NavLink>
                </li>

                {/* Dropdown Formulaires */}
                <li ref={dropdownRef} className="relative">
                  <button
                    onClick={toggleDropdown}
                    className={`w-full flex items-center justify-between border-l-4 py-3 px-6 text-sm font-medium transition-colors duration-200 ${
                      dropdownOpen
                        ? "border-blue-400 bg-[#243e5e] text-white"
                        : "border-transparent text-gray-300 hover:bg-[#1a334f] hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <FiFileText className="h-4 w-4 flex-shrink-0 text-blue-200" />
                      Formulaires
                    </span>
                    <svg
                      className={`h-5 w-5 ml-2 transition-transform duration-300 ${
                        dropdownOpen ? "rotate-180" : "rotate-0"
                      }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                    {dropdownOpen && (
                      <ul className="w-full overflow-hidden bg-[#112d4b] animate-fadeIn">
                        {routesConfig
                          .filter((r) => {
                            // if (!r.enabled) return false; 

                            // Cas spécial : Form FEESO
                            if (r.label === "Form FEESO") {
                              return (
                                existMembre === "exist dans FEESO" ||
                                checkGroupAdmin ||
                                checkGroupSeancePlacement
                              );
                            }

                            // Cas spécial : Form SCFP
                            if (r.label === "Form SCFP") {
                              return (
                                existMembre === "exist dans SCFP" ||
                                checkGroupAdmin ||
                                checkGroupSeancePlacement
                              );
                            }

                            // Cas spécial : Form AEFO
                            if (r.label === "Form AEFO") {
                              return (
                                existMembre === "exist dans AEFO" ||
                                checkGroupAdmin ||
                                checkGroupSeancePlacement
                              );
                            }

                            // Toutes les autres routes
                            return true;
                          })
                          .sort((a, b) => a.label.localeCompare(b.label, "fr"))
                          .map(({ path, label }) => (
                            <NavLink
                              key={path}
                              to={path}
                              className={({ isActive }) =>
                                `block border-l-4 py-2.5 pl-[3.25rem] pr-6 text-sm transition-colors duration-200 ${
                                  isActive
                                    ? "border-blue-400 bg-[#243e5e] text-white"
                                    : "border-transparent text-gray-300 hover:bg-[#1a334f] hover:text-white"
                                }`
                              }
                            >
                              {label}
                            </NavLink>
                          ))}
                      </ul>
                    )}
                  {/* {dropdownOpen && (
                    <ul className="mt-2 w-full bg-gray-700 rounded-2xl shadow-lg overflow-hidden animate-fadeIn">
                      {routesConfig
                        .filter((r) => r.enabled)
                        .map(({ path, label }) => (
                          <NavLink
                            key={path}
                            to={path}
                            className={({ isActive }) =>
                              `text-sm py-2 px-4 block rounded-xl transition-all duration-300 ${
                                isActive
                                  ? "bg-blue-600 text-white shadow-inner"
                                  : "text-white bg-gray-700 hover:bg-gray-600"
                              }`
                            }
                          >
                            {label}
                          </NavLink>
                        ))}
                    </ul>
                  )} */}
                </li>

                {/* Liens fixes */}
                {[
                  { path: "/demande", label: "Mes demandes", icon: FiInbox },
                  { path: "/taches", label: "Mes tâches", icon: FiCheckSquare },
                ].map(({ path, label, icon: Icon }) => (
                  <li key={path}>
                    <NavLink
                      to={path}
                      className={({ isActive }) =>
                        `flex w-full items-center gap-3 border-l-4 py-3 px-6 text-sm font-medium transition-colors duration-200 ${
                          isActive
                            ? "border-blue-400 bg-[#243e5e] text-white"
                            : "border-transparent text-gray-300 hover:bg-[#1a334f] hover:text-white"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 flex-shrink-0 text-blue-200" />
                      {label}
                    </NavLink>
                  </li>
                ))}

                {(checkGroupAdmin || checkGroupRecMensuelle ||checkGroupInfoBancaire || checkGroupAntCriminels) && (
                  <li>
                    <NavLink
                      to="/Archives"
                      className={({ isActive }) =>
                        `flex w-full items-center gap-3 border-l-4 py-3 px-6 text-sm font-medium transition-colors duration-200 ${
                          isActive
                            ? "border-blue-400 bg-[#243e5e] text-white"
                            : "border-transparent text-gray-300 hover:bg-[#1a334f] hover:text-white"
                        }`
                      }
                    >
                      <FiArchive className="h-4 w-4 flex-shrink-0 text-blue-200" />
                      Archives
                    </NavLink>
                  </li>
                )}


                {/* Admin-only link */}
                {checkGroupAdmin && (
                  <li>
                    <NavLink
                      to="/groupRoles"
                      className={({ isActive }) =>
                        `flex w-full items-center gap-3 border-l-4 py-3 px-6 text-sm font-medium transition-colors duration-200 ${
                          isActive
                            ? "border-blue-400 bg-[#243e5e] text-white"
                            : "border-transparent text-gray-300 hover:bg-[#1a334f] hover:text-white"
                        }`
                      }
                    >
                      <FiUsers className="h-4 w-4 flex-shrink-0 text-blue-200" />
                      Groupes
                    </NavLink>
                  </li>
                )}
              </>
            )}
          </ul>
        </nav>
      </aside>

      {/* Main Content Area */}

      <main
        className={`min-w-0 flex-1 ${
          location.pathname === "/" ? "bg-[#f5f6f8]" : ""
        }`}
      >
        <nav className="sticky top-0 z-20 flex min-h-[64px] items-center justify-between border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
          <div className="flex min-w-0 items-center gap-3">
            {isSignedIn && !mobileOpen && (
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 md:hidden"
                aria-label="Ouvrir la navigation"
                aria-controls="mobile-navigation"
                aria-expanded={mobileOpen}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isSignedIn && (checkGroupAdmin || checkGroupRecMensuelle || checkGroupSeancePlacement || checkGroupAntCriminels || checkGroupTeletravail) && (
              <NavLink
                to="/gestionDonnéesFinancières"
                title="Paramètres Finance"
                className={({ isActive }) =>
                  `inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors duration-200 ${
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`
                }
              >
                <FiSettings size={19} />
              </NavLink>
            )}

            <div className="flex min-h-10 items-center gap-2.5 border-l border-gray-200 pl-3">
              {isSignedIn ? (
                <>
                  <Login className="login" loginView="avatar" />
                  <div className="hidden min-w-0 sm:block">
                    <p className="max-w-52 truncate text-sm font-semibold leading-5 text-gray-900">
                      {user?.displayName || "Mon compte"}
                    </p>
                    <p className="max-w-52 truncate text-xs font-medium leading-4 text-blue-700">
                      {roleLabel}
                    </p>
                  </div>
                </>
              ) : (
                <MicrosoftSignInButton label="Se connecter" compact />
              )}
            </div>
          </div>
        </nav>
        <>
          {showWarning && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white p-6 rounded shadow text-center">
                <p className="text-lg font-semibold mb-4">
                  Vous serez déconnecté dans{" "}
                  <span className="text-red-600 font-bold">{countdown}</span>{" "}
                  secondes.
                </p>
              </div>
            </div>
          )}
        </>
        <div className="app">
          <header></header>
        </div>
        <Outlet context={{ openFormsMenu }} />
      </main>
    </div>
  );
}

export default Header;
